import { useState, useMemo, useCallback } from 'react';

// Custom hook for table state management (sorting, filtering, pagination)
export const useExpenseTable = (initialExpenses = []) => {
	const [sortConfig, setSortConfig] = useState({
		key: 'date',
		direction: 'desc',
	});
	const [filters, setFilters] = useState({
		search: '',
		categories: [],
		dateRange: { start: '', end: '' },
		amountRange: { min: 0, max: Infinity },
	});
	const [selectedIds, setSelectedIds] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 10;

	// Apply filters
	const filteredExpenses = useMemo(() => {
		return initialExpenses.filter((expense) => {
			// Search filter (name and notes)
			if (filters.search) {
				const searchLower = filters.search.toLowerCase();
				const matchesSearch =
					expense.name.toLowerCase().includes(searchLower) ||
					(expense.notes && expense.notes.toLowerCase().includes(searchLower));
				if (!matchesSearch) return false;
			}

			// Category filter
			if (filters.categories.length > 0) {
				if (!filters.categories.includes(expense.category)) return false;
			}

			// Date range filter
			if (filters.dateRange.start && expense.date < filters.dateRange.start)
				return false;
			if (filters.dateRange.end && expense.date > filters.dateRange.end)
				return false;

			// Amount range filter
			if (
				expense.amount < filters.amountRange.min ||
				expense.amount > filters.amountRange.max
			)
				return false;

			return true;
		});
	}, [initialExpenses, filters]);

	// Apply sorting
	const sortedExpenses = useMemo(() => {
		const sorted = [...filteredExpenses];

		sorted.sort((a, b) => {
			const aValue = a[sortConfig.key];
			const bValue = b[sortConfig.key];

			if (aValue === bValue) return 0;

			const comparison = aValue < bValue ? -1 : 1;
			return sortConfig.direction === 'asc' ? comparison : -comparison;
		});

		return sorted;
	}, [filteredExpenses, sortConfig]);

	// Apply pagination
	const paginatedExpenses = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		return sortedExpenses.slice(startIndex, startIndex + pageSize);
	}, [sortedExpenses, currentPage]);

	// Total pages
	const totalPages = Math.ceil(sortedExpenses.length / pageSize);

	// Handle sort
	const handleSort = useCallback((key) => {
		setSortConfig((prev) => ({
			key,
			direction:
				prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
		}));
		setCurrentPage(1); // Reset to first page
	}, []);

	// Handle filter updates
	const updateFilters = useCallback((newFilters) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
		setCurrentPage(1); // Reset to first page
	}, []);

	// Handle selection
	const toggleSelection = useCallback((id) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
		);
	}, []);

	const toggleSelectAll = useCallback(() => {
		if (selectedIds.length === paginatedExpenses.length) {
			setSelectedIds([]);
		} else {
			setSelectedIds(paginatedExpenses.map((e) => e.id));
		}
	}, [selectedIds, paginatedExpenses]);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
	}, []);

	// Calculate total
	const total = useMemo(() => {
		return sortedExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
	}, [sortedExpenses]);

	return {
		// Data
		expenses: paginatedExpenses,
		allExpenses: sortedExpenses,
		total,

		// Sorting
		sortConfig,
		handleSort,

		// Filtering
		filters,
		updateFilters,

		// Selection
		selectedIds,
		toggleSelection,
		toggleSelectAll,
		clearSelection,
		isAllSelected:
			paginatedExpenses.length > 0 &&
			selectedIds.length === paginatedExpenses.length,

		// Pagination
		currentPage,
		totalPages,
		setCurrentPage,
		pageSize,
	};
};
